/* ==========================================================================
   IRON FIT — Machine page controller

   Spin viewer: commercial-360 style. Frames are SWAPPED, never blended —
   one image visible at a time, driven by a continuous rotation value with
   drag, flick inertia and eased button steps, all on requestAnimationFrame.
   Smoothness scales with frame count: drop more photos into the frames
   array (or raise `count` on a pattern) and the same viewer runs at the
   density of a tenten-style turntable. No other change needed.

   Add-ons: the accessories the old store sold with machines — bench, 7 ft
   bar, dumbbell set, rubber plates per kg — picked with big toggles, summed
   into a live total, and written into the WhatsApp order message.
   ========================================================================== */
(function () {
  'use strict';

  var WA_NUMBER = '96181923182';

  /* Accessory prices from the live store's own addon feed (products.json):
     bar $120 · dumbbell set $120 · bench $180 · rubber plates $3/kg. */
  var ADDONS = {
    bench: { name: 'Commercial Adjustable Bench', price: 180, img: 'assets/04-commercial-adjustable-bench-180-delivered-760.jpg' },
    bar:   { name: '7 FT Olympic Bar + Collars',  price: 120, img: 'assets/05-olympic-bar-120-delivered-760.jpg' },
    dbset: { name: '50 KG 3-in-1 Dumbbell / Barbell Set', price: 120, img: 'assets/dbset-open-case-contents-760.jpg' }
  };
  var PLATE_PER_KG = 3;      /* standalone rate — $2.5/kg is the package rate */

  var PRODUCTS = {
    compact: {
      kicker: 'Small space',
      name: 'Compact Single-Stack Gym',
      price: 570,
      frames: { pattern: 'assets/spin-compact-{n}-1040.jpg', count: 1 },
      promise: 'The price covers <b>everything</b> — the machine, delivery to your home, and our team installing and testing it. You pay nothing else.',
      addons: ['bench', 'bar', 'dbset'],
      features: [
        'Smallest footprint we sell — 94 × 100 cm, under 1 m² of floor',
        'Built-in weight stack — no loose plates to buy or store',
        'Dips, pull-ups, lat pulldowns, low rows and cable exercises',
        'Made for apartments and small rooms'
      ],
      included: [
        'The machine',
        'Delivery to your home — all Lebanon',
        'Professional installation, assembly and testing'
      ],
      notIncluded: ['Bench, Olympic bar, plates and dumbbells — add them below if you want them'],
      dims: [{ v: '226 cm', k: 'Height' }, { v: '94 cm', k: 'Width' }, { v: '100 cm', k: 'Depth' }],
      /* Tripo export, 57 MB as it came. Fine over localhost, far too heavy to
         ship — it needs decimating before this goes anywhere near live. */
      model: 'assets/ifz-compact-3d.glb?v=4',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '226 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '94 cm',  a: [0.16, 0.02, 0.50], b: [0.84, 0.02, 0.50] },
        depth:  { l: '100 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Lat pulldown bar',      p: [0.50, 0.96, 0.50] },
        { l: 'Built-in weight stack', p: [0.52, 0.34, 0.42] },
        { l: 'Leg rollers',           p: [0.74, 0.22, 0.34] }
      ],
      others: ['crossover', 'smith', 'bench']
    },
    crossover: {
      kicker: 'Best seller',
      name: 'Dual Cable Crossover',
      price: 760,
      frames: { pattern: 'assets/spin-cable-{n}.jpg', count: 4 },
      promise: 'The price covers <b>everything</b> — the machine, a 7 ft Olympic bar with collars, delivery, and our team installing and testing it. You pay nothing else.',
      addons: ['bench', 'dbset'],            /* bar already included */
      features: [
        'Two separate 75 kg weight stacks — train both sides',
        'Two cable stations you can set to any height',
        'Pull-up bar with several grip positions',
        'Heavy-duty steel frame, powder-coated'
      ],
      included: [
        'The machine',
        'One 7 ft Olympic bar with collars',
        'Delivery to your home — all Lebanon',
        'Professional installation, assembly and testing'
      ],
      notIncluded: ['Bench, plates and dumbbells — add them below if you want them', 'This is the cable machine — it has no Smith bar'],
      dims: [{ v: '210 cm', k: 'Height' }, { v: '150 cm', k: 'Width' }, { v: '120 cm', k: 'Depth' }, { v: '2 × 75 kg', k: 'Weight stacks' }],
      model: 'assets/ifz-crossover-3d.glb?v=4',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '210 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '150 cm', a: [0.06, 0.02, 0.50], b: [0.94, 0.02, 0.50] },
        depth:  { l: '120 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Multi-grip pull-up bar', p: [0.50, 0.97, 0.50] },
        { l: '2 × 75 kg stacks',       p: [0.42, 0.30, 0.42] },
        { l: 'Adjustable pulleys',     p: [0.14, 0.70, 0.60] }
      ],
      others: ['compact', 'smith', 'plates']
    },
    smith: {
      kicker: 'Complete system',
      name: 'Multifunctional Smith Machine',
      price: 1059,
      frames: { pattern: 'assets/spin-smith-{n}.jpg', count: 5 },
      promise: 'The price covers <b>everything</b> — the machine, delivery to your home, and our team installing and testing it. You pay nothing else.',
      addons: ['bench', 'bar', 'dbset'],
      features: [
        'Guided Smith bar built into the machine',
        'Two cable stations with adjustable pulleys',
        'J-hooks for free-bar work and storage horns for your plates',
        'One frame that covers every big lift'
      ],
      included: [
        'The machine, with its built-in Smith bar',
        'Delivery to your home — all Lebanon',
        'Professional installation, assembly and testing'
      ],
      notIncluded: ['Bench, plates and dumbbells — add them below if you want them', 'No separate loose 7 ft bar — the Smith bar is part of the machine'],
      dims: [{ v: '210 cm', k: 'Height' }, { v: '150 cm', k: 'Width' }, { v: '118 cm', k: 'Depth' }],
      model: 'assets/ifz-smith-3d.glb?v=4',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '210 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '150 cm', a: [0.04, 0.02, 0.50], b: [0.96, 0.02, 0.50] },
        depth:  { l: '118 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Guided Smith bar',   p: [0.50, 0.72, 0.30] },
        { l: 'Cable stations',     p: [0.10, 0.66, 0.60] },
        { l: 'J-hooks & horns',    p: [0.88, 0.40, 0.60] }
      ],
      others: ['compact', 'crossover', 'bench']
    },

    /* ------------------------------------------------------------------
       Accessories. Same template, same spin viewer. `accessory: true` means
       the page drops the add-on picker (you don't bolt a bench onto a bench)
       and swaps "See it spin & order" language for a plain delivered price.
       ------------------------------------------------------------------ */
    bench: {
      accessory: true,
      kicker: 'Bench',
      name: 'Commercial Adjustable Bench',
      price: 180,
      frames: { pattern: 'assets/spin-bench-{n}.jpg', count: 5 },
      promise: 'The price covers <b>the bench and delivery to your home</b>. You pay cash at the door.',
      addons: [],
      features: [
        'Adjusts from flat to full upright',
        'Commercial-grade padding and steel frame',
        'Leg anchor for decline and sit-up work',
        'Works with every Iron Fit machine'
      ],
      included: ['The bench', 'Delivery to your home — all Lebanon'],
      notIncluded: ['No installation needed — it arrives ready to use'],
      dims: [{ v: 'Flat to upright', k: 'Backrest' }],
      others: ['bar', 'dbset']
    },

    bar: {
      accessory: true,
      kicker: 'Barbell',
      name: '7 FT Olympic Bar + Collars',
      price: 120,
      frames: { pattern: 'assets/spin-bar-{n}.jpg', count: 5 },
      promise: 'The price covers <b>the bar, its collars and delivery</b>. You pay cash at the door.',
      addons: [],
      features: [
        'Full 7 ft Olympic length',
        'Comes with a pair of collars',
        'Knurled grip, rotating sleeves',
        'Fits all standard Olympic plates'
      ],
      included: ['The 7 ft Olympic bar', 'A pair of collars', 'Delivery to your home — all Lebanon'],
      notIncluded: ['Plates are sold separately — $3 per kg'],
      dims: [{ v: '7 ft', k: 'Length' }],
      others: ['bench', 'plates']
    },

    dbset: {
      accessory: true,
      kicker: 'Dumbbells',
      name: '50 KG 3-in-1 Dumbbell / Barbell Set',
      price: 120,
      frames: { pattern: 'assets/spin-dbset-{n}.jpg', count: 5 },
      promise: 'The price covers <b>the full 50 kg set and delivery</b>. You pay cash at the door.',
      addons: [],
      features: [
        'Two dumbbells, or joined into one barbell',
        '50 kg of plates in the set',
        'Arrives in its own carry case',
        'Screw collars included'
      ],
      included: ['The complete 50 kg set', 'Carry case', 'Delivery to your home — all Lebanon'],
      notIncluded: ['No bench or rack included'],
      dims: [{ v: '50 kg', k: 'Total weight' }, { v: '3-in-1', k: 'Dumbbell / barbell' }],
      others: ['bench', 'bar']
    },

    plates: {
      accessory: true,
      perKg: true,
      kicker: 'Plates',
      name: 'Rubber Olympic Plates',
      price: 3,
      frames: { pattern: 'assets/spin-plates-{n}.jpg', count: 4 },
      promise: 'Rubber plates are <b>$3 per kg, delivered</b>. Buy exactly the weight you want — pick it below and the total works itself out.',
      addons: [],
      features: [
        'Rubber coated — quiet, and kind to your floor',
        'Standard Olympic centre, fits any 7 ft bar',
        'Available in 5, 10 and 20 kg plates',
        'Bought by the kilo — pay only for what you take'
      ],
      included: ['The plates you choose', 'Delivery to your home — all Lebanon'],
      notIncluded: ['Bar sold separately — $120 with collars'],
      dims: [{ v: '5 / 10 / 20 kg', k: 'Plate sizes' }, { v: '$3', k: 'Per kg' }],
      others: ['bar', 'dbset']
    }
  };

  function $(s) { return document.querySelector(s); }
  function money(n) { return '$' + n.toLocaleString('en-US'); }

  /* The cart shows a photo of every line now. Every product here is already
     described by its spin frames, so frame 1 is the thumbnail — no second
     list of images to keep in step with the first. */
  function thumbOf(p) {
    if (!p || !p.frames || !p.frames.pattern) return '';
    return window.IFZA(p.frames.pattern.replace('{n}', '1'));
  }

  /* Which product this page is.

     Static site: the ?m= parameter.
     Shopify: there is no such parameter — the theme knows which product it
     is rendering and tells us, because a handle is the only thing that
     survives a product being renamed. See sections/ifz-product.liquid. */
  function key() {
    if (window.IFZ_PRODUCT_KEY && PRODUCTS[window.IFZ_PRODUCT_KEY]) {
      return window.IFZ_PRODUCT_KEY;
    }
    var m = new URLSearchParams(location.search).get('m');
    return PRODUCTS[m] ? m : 'crossover';
  }

  function frameList(f) {
    var out = [];
    for (var i = 1; i <= f.count; i++) out.push(f.pattern.replace('{n}', i));
    return out;
  }

  /* ------------------------------------------------------------------
     Order state → total → WhatsApp message
     ------------------------------------------------------------------ */
  var state = { picked: {}, plates: 0 };

  function total(p) {
    /* Plates are the product itself here, not an add-on: the price IS per kg. */
    if (p.perKg) return state.plates * p.price;
    var t = p.price;
    p.addons.forEach(function (k) { if (state.picked[k]) t += ADDONS[k].price; });
    t += state.plates * PLATE_PER_KG;
    return t;
  }

  function waLink(p) {
    var lines;
    if (p.perKg) {
      lines = state.plates > 0
        ? ['Hi! I want ' + state.plates + ' kg of rubber Olympic plates ($' + p.price + ' per kg).']
        : ['Hi! I want to order rubber Olympic plates ($' + p.price + ' per kg). The weight I want is: '];
    } else {
      lines = ['Hi! I want the ' + p.name + ' (' + money(p.price) + ').'];
      p.addons.forEach(function (k) {
        if (state.picked[k]) lines.push('+ ' + ADDONS[k].name + ' (' + money(ADDONS[k].price) + ')');
      });
      if (state.plates > 0) lines.push('+ ' + state.plates + ' kg rubber plates (' + money(state.plates * PLATE_PER_KG) + ')');
    }
    if (!p.perKg || state.plates > 0) {
      lines.push('Total: ' + money(total(p)) + ' all-in, cash on delivery.');
    }
    lines.push('My name is: ');
    return 'https://wa.me/' + WA_NUMBER + '?text=' + encodeURIComponent(lines.join('\n'));
  }

  var barSync = null;              /* set once the phone buy bar is built */

  function refreshOrder(p) {
    $('#totalN').textContent = money(total(p));
    var link = waLink(p);
    /* The order block's WhatsApp button is now the cash-on-delivery button,
       so there may be nothing here to point at. The one in the header is
       untouched — asking a question before buying still goes to a person. */
    var wa = $('#waOrder');
    if (wa) wa.href = link;
    $('#waTop').href = link;
    if (barSync) barSync();
  }

  /* ------------------------------------------------------------------
     Phone buy bar

     The spin viewer is the whole first screen on a phone, which put the
     price and "Add to cart" two screens below the fold. A pinned bar
     carries them instead — the same one the packages have.

     It does not own any state: it reads the total that refreshOrder just
     wrote, and it buys by clicking the page's own button, so the weight
     warning and the added state come along for free. It steps out of the
     way once the real order block is on screen, so there is never a pair
     of Add-to-cart buttons on the same screen.
     ------------------------------------------------------------------ */
  function buyBar(p) {
    var cart = document.getElementById('addCart');
    var order = document.getElementById('orderBox') || (cart && cart.closest('.order'));
    if (!cart) return;

    var bar = document.createElement('div');
    bar.className = 'buybar';
    bar.innerHTML =
      '<div class="buybar__tx"><b></b><div class="buybar__pr"><i></i><em>All-in</em></div></div>' +
      '<button class="buybar__cta" type="button">Add to cart</button>';
    bar.querySelector('b').textContent = p.name;
    document.body.appendChild(bar);
    /* the page's own bottom padding is added by class, so the pages that
       load this stylesheet without a bar (fit.html) keep their spacing */
    document.body.classList.add('has-buybar');

    var totalEl = bar.querySelector('i');
    var cta = bar.querySelector('.buybar__cta');

    barSync = function () {
      totalEl.textContent = $('#totalN').textContent;
      cta.classList.toggle('is-added', cart.classList.contains('is-added'));
      cta.classList.toggle('is-warn', cart.classList.contains('is-warn'));
      cta.textContent = cart.classList.contains('is-added') ? 'Added! View cart →'
                      : cart.classList.contains('is-warn') ? 'Pick a weight first'
                      : 'Add to cart';
    };

    cta.addEventListener('click', function () {
      cart.click();
      barSync();
      /* the weight warning puts itself back after 1.6s — follow it */
      setTimeout(barSync, 1700);
    });

    if (order && window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        bar.classList.toggle('is-away', entries[0].isIntersecting);
      }, { rootMargin: '-40px 0px -120px 0px' }).observe(order);
    }

    barSync();
  }

  /* ------------------------------------------------------------------
     Static content
     ------------------------------------------------------------------ */
  function fill(p) {
    document.title = 'Iron Fit — ' + p.name;
    if (window.IFZPixel) window.IFZPixel.viewContent(p.name, p.price);
    $('#kicker').textContent = p.kicker;
    $('#name').textContent = p.name;
    $('#price').textContent = p.perKg ? ('$' + p.price) : money(p.price);
    $('#promise').innerHTML = p.promise;
    inTheBox(p);

    /* The fit checker only carries the three machines, so hide its entry
       point on accessory pages and carry the machine through on the rest. */
    var fitLink = document.getElementById('fitLink');
    if (fitLink) {
      if (p.accessory) fitLink.hidden = true;
      else fitLink.href = 'fit.html?m=' + key();
    }

    /* Accessories are delivered, not installed, and plates are priced by the
       kilo — so the badge next to the price has to say the right thing. */
    var tag = document.querySelector('.price__tag');
    if (tag) {
      tag.textContent = p.perKg ? 'Per kg · delivered'
                     : p.accessory ? 'Delivered · final price'
                     : 'All-in · final price';
    }

    var f = $('#features'), inc = $('#included'), no = $('#notIncluded');
    p.features.forEach(function (t) { var li = document.createElement('li'); li.textContent = t; f.appendChild(li); });
    p.included.forEach(function (t) { var li = document.createElement('li'); li.textContent = t; inc.appendChild(li); });
    p.notIncluded.forEach(function (t) { var li = document.createElement('li'); li.textContent = t; no.appendChild(li); });

    var dims = $('#dims');
    p.dims.forEach(function (d) {
      var s = document.createElement('span');
      s.textContent = d.v;
      var small = document.createElement('small');
      small.textContent = d.k;
      s.appendChild(small);
      dims.appendChild(s);
    });

    /* The machine page no longer carries this section — see ifz-product.liquid.
       Accessory pages still do, so this builds only when the row is there. */
    var row = $('#moreRow');
    if (!row) return;
    p.others.forEach(function (ok) {
      var o = PRODUCTS[ok];
      var a = document.createElement('a');
      a.className = 'more__card';
      a.href = 'machine.html?m=' + ok;
      var th = document.createElement('div'); th.className = 'more__thumb';
      /* a 74px square does not need the full studio master */
      var small = frameList(o.frames)[0].replace(/^(assets\/spin-[a-z]+-\d+)\.jpg$/i, '$1-760.jpg');
      th.appendChild(window.IFZPic(small, o.name));
      var div = document.createElement('div');
      var b = document.createElement('b'); b.textContent = o.name;
      var span = document.createElement('span');
      span.textContent = o.perKg ? ('$' + o.price + ' per kg')
                       : money(o.price) + (o.accessory ? ' delivered' : ' all-in');
      div.appendChild(b); div.appendChild(span);
      a.appendChild(th); a.appendChild(div);
      row.appendChild(a);
    });
  }


  /* ------------------------------------------------------------------
     WHAT COMES WITH IT, IN PICTURES

     The crossover ships with a 7 ft Olympic bar. That was written twice —
     in the promise line and in "the price includes" — and people still
     asked, because nobody reads a price page, they scan it. A photograph of
     the bar next to the word INCLUDED answers it at a glance in a way two
     more sentences never will.

     Driven off the machine's own `included` list rather than a hand-written
     list per product, so a machine that stops shipping with something stops
     showing it here too.
     ------------------------------------------------------------------ */
  var BOX_PICS = [
    { test: /olympic bar|7 ft bar|bar with collars/i,
      img: 'assets/spin-bar-1-760.jpg', name: '7 FT Olympic Bar', note: 'with collars' },
    { test: /adjustable bench/i,
      img: 'assets/spin-bench-1-760.jpg', name: 'Adjustable Bench', note: '' },
    { test: /dumbbell/i,
      img: 'assets/spin-dbset-1-760.jpg', name: 'Dumbbell Set', note: '' },
    { test: /rubber olympic plates|kg of plates/i,
      img: 'assets/spin-plates-1-760.jpg', name: 'Rubber Plates', note: '' }
  ];

  function inTheBox(p) {
    var host = $('#promise');
    if (!host || !p.included) return;

    var hits = BOX_PICS.filter(function (b) {
      return p.included.some(function (line) { return b.test.test(line); });
    });
    if (!hits.length) return;                 /* just a machine on its own */

    var box = document.createElement('div');
    box.className = 'inbox';
    box.innerHTML = '<span class="inbox__lead">Included with the machine — ' +
                    'no extra charge</span>';

    var row = document.createElement('div');
    row.className = 'inbox__row';

    hits.forEach(function (b) {
      var item = document.createElement('span');
      item.className = 'inbox__item';
      var media = document.createElement('span');
      media.className = 'inbox__pic';
      media.appendChild(window.IFZPic(b.img, b.name));
      var tx = document.createElement('span');
      tx.className = 'inbox__tx';
      tx.innerHTML = '<b>' + b.name + '</b>' + (b.note ? '<small>' + b.note + '</small>' : '');
      var tick = document.createElement('span');
      tick.className = 'inbox__tick';
      tick.textContent = '✓ Included';
      item.appendChild(media);
      item.appendChild(tx);
      item.appendChild(tick);
      row.appendChild(item);
    });

    box.appendChild(row);
    host.parentNode.insertBefore(box, host.nextSibling);
  }

  /* ------------------------------------------------------------------
     Add-ons UI
     ------------------------------------------------------------------ */
  function buildExtras(p) {
    var box = $('#extras');

    p.addons.forEach(function (k) {
      var a = ADDONS[k];
      var el = document.createElement('div');
      el.className = 'addon';
      el.setAttribute('role', 'checkbox');
      el.setAttribute('aria-checked', 'false');
      el.tabIndex = 0;

      var im = document.createElement('div'); im.className = 'addon__img';
      im.appendChild(window.IFZPic(a.img, a.name));

      var mid = document.createElement('div');
      var nm = document.createElement('div'); nm.className = 'addon__name'; nm.textContent = a.name;
      var pr = document.createElement('div'); pr.className = 'addon__price'; pr.textContent = '+ ' + money(a.price) + ' · delivered with the machine';
      mid.appendChild(nm); mid.appendChild(pr);

      var tick = document.createElement('div'); tick.className = 'addon__box';

      el.appendChild(im); el.appendChild(mid); el.appendChild(tick);

      function toggle() {
        state.picked[k] = !state.picked[k];
        el.classList.toggle('is-on', !!state.picked[k]);
        el.setAttribute('aria-checked', state.picked[k] ? 'true' : 'false');
        refreshOrder(p);
      }
      el.addEventListener('click', toggle);
      el.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); }
      });

      box.appendChild(el);
    });

    /* plates — same pairs picker as the packages, at the standalone rate.
       On the plates page itself the picker IS the product, so it stays; on
       the bench / bar / dumbbell pages there is nothing to add plates to. */
    if (p.accessory && !p.perKg) {
      var sec = box.closest('.extras');
      if (sec) sec.hidden = true;
      return;
    }

    var head = document.createElement('p');
    head.className = 'pkc__exnote';
    head.innerHTML = p.perKg
      ? 'Pick the weight you want in pairs of 5, 10 or 20 kg plates — up to 80 kg. ' +
        'Everything is <b>$' + p.price + ' per kg</b>, delivered to your door.'
      : 'Rubber Olympic plates are <b>$' + PLATE_PER_KG + ' per kg</b>, delivered with your ' +
        'machine at no extra delivery charge. Pick pairs of 5, 10 or 20 kg plates — up to 80 kg.';
    box.appendChild(head);

    var platesBox = document.createElement('div');
    box.appendChild(platesBox);

    window.IFZPlates(platesBox, function (kgNow) {
      state.plates = kgNow;
      refreshOrder(p);
    });
  }

  /* ------------------------------------------------------------------
     Spin viewer — frame swap + inertia, rAF-driven
     ------------------------------------------------------------------ */
  function viewer(p) {
    var stage = $('#stage');
    var hint = $('#hint');
    var fillBar = $('#spinFill');
    var note = $('#spinNote');
    var srcs = frameList(p.frames);
    var N = srcs.length;
    var imgs = [];

    /* Each frame is a <picture>: WebP for the browsers that take it, the same
       JPEG for the ones that don't. The viewer keeps working on the <img>
       inside, so none of the spin logic below knows the difference. */
    srcs.forEach(function (src, i) {
      var pic = window.IFZPic(src, i === 0 ? p.name : '');
      var img = pic.querySelector('img');
      img.draggable = false;
      if (i === 0) img.className = 'is-on';
      stage.appendChild(pic);
      imgs.push(img);
    });


    if (N <= 1) {
      hint.textContent = 'Photo of the real machine';
      fillBar.style.width = '100%';
      note.textContent = 'More angles of this machine coming soon';
      return;
    }

    note.textContent = 'Drag to spin · real photos, ' + N + ' angles';

    var pos = 0;          /* continuous rotation, 0 .. N-1 */
    var shown = 0;
    var vel = 0;
    var raf = null;
    var mode = 'idle';    /* idle | drag | inertia | anim */
    var animFrom = 0, animTo = 0, animT0 = 0, animDur = 0;

    function render() {
      var idx = Math.round(Math.min(N - 1, Math.max(0, pos)));
      if (idx !== shown) {
        imgs[shown].classList.remove('is-on');
        imgs[idx].classList.add('is-on');
        shown = idx;
        fillBar.style.width = (((idx + 1) / N) * 100).toFixed(1) + '%';
      }
    }

    function loop(ts) {
      if (mode === 'inertia') {
        pos += vel;
        vel *= 0.90;
        if (pos < 0)     { pos = 0;     vel = 0; }
        if (pos > N - 1) { pos = N - 1; vel = 0; }
        render();
        if (Math.abs(vel) < 0.012) {
          mode = 'anim';
          animFrom = pos; animTo = Math.round(pos);
          animT0 = ts; animDur = 140;
        }
      } else if (mode === 'anim') {
        var t = Math.min(1, (ts - animT0) / animDur);
        var e = 1 - Math.pow(1 - t, 3);
        pos = animFrom + (animTo - animFrom) * e;
        render();
        if (t >= 1) { pos = animTo; mode = 'idle'; }
      }
      if (mode !== 'idle') { raf = requestAnimationFrame(loop); }
      else raf = null;
    }

    function run() { if (!raf) raf = requestAnimationFrame(loop); }

    function animateTo(target, dur) {
      animFrom = pos;
      animTo = Math.min(N - 1, Math.max(0, target));
      animT0 = performance.now();
      animDur = dur || 240;
      mode = 'anim';
      run();
    }

    var hintGone = false;
    function dismissHint() {
      if (hintGone) return;
      hintGone = true;
      hint.classList.add('is-gone');
    }

    $('#spinL').addEventListener('click', function () { dismissHint(); animateTo(Math.round(pos) - 1); });
    $('#spinR').addEventListener('click', function () { dismissHint(); animateTo(Math.round(pos) + 1); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { dismissHint(); animateTo(Math.round(pos) - 1); }
      if (e.key === 'ArrowRight') { dismissHint(); animateTo(Math.round(pos) + 1); }
    });

    /* Drag: the full stage width sweeps the full arc, like real turntables.
       Velocity is tracked for flick inertia on release. */
    var dragging = false, lastX = 0, lastT = 0;

    stage.addEventListener('pointerdown', function (e) {
      dragging = true;
      lastX = e.clientX; lastT = performance.now();
      vel = 0; mode = 'drag';
      stage.classList.add('is-dragging');
      stage.setPointerCapture(e.pointerId);
      dismissHint();
    });

    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var pxPerFrame = stage.getBoundingClientRect().width / (N + 1);
      var now = performance.now();
      var dx = e.clientX - lastX;
      var dpos = dx / pxPerFrame;
      pos = Math.min(N - 1 + 0.45, Math.max(-0.45, pos + dpos));
      vel = dpos * Math.min(1, 16 / Math.max(1, now - lastT));
      lastX = e.clientX; lastT = now;
      render();
    });

    ['pointerup', 'pointercancel'].forEach(function (t) {
      stage.addEventListener(t, function () {
        if (!dragging) return;
        dragging = false;
        stage.classList.remove('is-dragging');
        mode = 'inertia';
        run();
      });
    });

    /* Intro: wait for every frame to decode, then one eased sweep out and
       back so the page opens moving. Skipped the moment the user touches. */
    Promise.all(imgs.map(function (im) {
      return im.decode ? im.decode().catch(function () {}) : Promise.resolve();
    })).then(function () {
      if (hintGone) return;
      animateTo(N - 1, 900);
      setTimeout(function () { if (!hintGone) animateTo(0, 900); }, 1050);
    });
  }


  /* ------------------------------------------------------------------
     3D model — the viewer itself lives in ifz-3d.js, shared with the
     package pages so there is one copy of the maths, not two.
     ------------------------------------------------------------------ */
  function build3d(p) {
    if (!window.IFZ3D) return;
    var spec = window.IFZ3D.spec(key());
    if (!spec) return;
    var spin = $('.spin');
    if (!spin || !spin.parentNode) return;
    var slot = document.createElement('div');
    spin.parentNode.insertBefore(slot, spin.nextSibling);
    spec.name = p.name;
    window.IFZ3D.attach(spec, slot);
  }


  /* ------------------------------------------------------------------
     Add to cart
     ------------------------------------------------------------------ */
  function wireCart(p) {
    var btn = document.getElementById('addCart');
    if (!btn) return;
    var added = false;

    /* ------------------------------------------------------------------
       ORDERING, CASH ON DELIVERY

       "Order on WhatsApp" is now "Order now — cash on delivery", and it
       goes through the cart rather than opening a chat.

       Why the cart and not EasySell's own product-page button: this page
       lets people tick a bench, a bar, dumbbells and a weight of plates.
       EasySell's product button orders the PRODUCT — it knows nothing about
       those add-ons and would quietly drop every one of them. Putting the
       whole selection in the cart first and handing EasySell the cart is
       the only route that orders what was actually chosen.
       ------------------------------------------------------------------ */
    function add() {

      /* Plates are sold by weight, so the cart line is the weight itself
         rather than a $3 unit with the kilos bolted on as an extra. */
      if (p.perKg) {
        if (state.plates <= 0) {
          btn.classList.add('is-warn');
          btn.textContent = 'Pick a weight first';
          setTimeout(function () {
            btn.classList.remove('is-warn');
            btn.textContent = 'Add to cart';
          }, 1600);
          return false;
        }
        window.IFZCart.add({
          type: 'accessory',
          name: state.plates + ' kg Rubber Olympic Plates',
          price: state.plates * p.price,
          img: thumbOf(p),
          /* Shopify sells plates as individual 2.5/5/10/20 kg pieces, so the
             weight is carried as `kg` and split into pieces at order time. */
          key: 'plates', kg: state.plates,
          extras: [],
          includes: p.included
        });
      } else {
        var extras = [];
        p.addons.forEach(function (k) {
          /* `key` is what the order worker resolves to a Shopify variant.
             Every add-on becomes its own line, because that is what it is. */
          if (state.picked[k]) extras.push({ key: k, name: ADDONS[k].name, price: ADDONS[k].price });
        });
        if (state.plates > 0) {
          extras.push({ key: 'plates', kg: state.plates,
                        name: 'Rubber plates — ' + state.plates + ' kg',
                        price: state.plates * PLATE_PER_KG });
        }

        window.IFZCart.add({
          type: p.accessory ? 'accessory' : 'machine',
          name: p.name,
          price: p.price,
          img: thumbOf(p),
          key: key(),
          extras: extras,
          includes: p.included
        });
      }

      added = true;
      btn.classList.add('is-added');
      btn.textContent = '✓ Added! View cart →';
      return true;
    }

    btn.addEventListener('click', function () {
      if (added) { window.location.href = window.IFZU('cart-revamp.html'); return; }
      add();
    });

    var cod = document.getElementById('codOrder');
    if (cod) {
      cod.addEventListener('click', function () {
        /* add() returns false when it refused — plates with no weight picked
           — and in that case the button has already said so. */
        if (!added && add() === false) return;
    /* Tell the cart page this was "order now", not "add to cart". It opens
       EasySell's own button once the Shopify cart has finished syncing.

       Not /cart?easysell=1 — that is EasySell's own auto-open and it fires
       on page load, which races our sync. Opening the order form against a
       cart that is still half-written is how somebody gets asked to pay for
       the wrong thing. The cart page knows when the sync finished; this
       just tells it what the customer asked for. */
    try { sessionStorage.setItem('ifz-cod-go', '1'); } catch (e) {}
        window.location.href = window.IFZU('cart-revamp.html');
      });
    }
  }

  var p = PRODUCTS[key()];

  /* Shopify is the authority on price — it is what will actually be charged.
     Quoting anything else here is how a customer ends up arguing at the door,
     so the theme's price wins over the one in the table above. */
  if (window.IFZ_PRODUCT_PRICE) {
    var shopPrice = parseFloat(window.IFZ_PRODUCT_PRICE);
    if (shopPrice > 0) p.price = shopPrice;
  }
  fill(p);
  buildExtras(p);
  wireCart(p);
  build3d(p);
  buyBar(p);          /* before the first refresh, so the bar starts in sync */
  refreshOrder(p);
  viewer(p);
})();
